import React, { useEffect, useState } from "react";
import { Modal, Table, Input, Button, Space, message } from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import { updateDocumentMetadata } from "../../api/api";

interface MetadataModalProps {
  show: boolean;
  onHide: () => void;
  document: {
    id: string;
    title: string;
    dateModified: string;
    status: string;
    metadata?: { key: string; value: string }[];
  };
}

const MetadataModal: React.FC<MetadataModalProps> = ({ show, onHide, document }) => {
  const [metadata, setMetadata] = useState<{ key: string; value: string }[]>([]);
  const [keyInput, setKeyInput] = useState<string>("");
  const [valueInput, setValueInput] = useState<string>("");

  useEffect(() => {
    if (document?.metadata) {
      const existingTitleEntry = document.metadata.find(
        (item) => item.key?.toLowerCase() === "title"
      );

      const nonTitleMetadata = document.metadata.filter(
        (item) => item.key?.toLowerCase() !== "title"
      );

      const unifiedTitleValue = existingTitleEntry
        ? existingTitleEntry.value
        : document.title;

      const initialMetadata = [
        { key: "title", value: unifiedTitleValue },
        ...nonTitleMetadata,
      ];

      setMetadata(initialMetadata);
    }
  }, [document]);

  const handleAddMetadata = () => {
    if (keyInput && valueInput) {
      setMetadata((prevMetadata) => [
        ...prevMetadata,
        { key: keyInput, value: valueInput },
      ]);
      setKeyInput("");
      setValueInput("");
    }
  };

  const handleEditChange = (
    index: number,
    field: "key" | "value",
    value: string
  ) => {
    const newMetadata = [...metadata];
    newMetadata[index][field] = value;
    setMetadata(newMetadata);
  };

  const handleRemoveMetadata = (index: number) => {
    if (metadata[index].key?.toLowerCase() === "title") {
      message.warning("Title cannot be removed.");
      return;
    }
    setMetadata((prevMetadata) => prevMetadata.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    try {
      const titleEntry = metadata.find(
        (item) => item.key?.toLowerCase() === "title"
      );
      if (!titleEntry || !titleEntry.value.trim()) {
        message.error("Title cannot be empty.");
        return;
      }

      await updateDocumentMetadata(document.id, metadata);
      message.success("Metadata updated successfully");
      onHide();
    } catch (error) {
      console.error("Failed to update metadata:", error);
      message.error("Failed to update metadata");
    }
  };

  const columns = [
    {
      title: 'Key',
      dataIndex: 'key',
      key: 'key',
      width: '35%',
      render: (text: string, record: any, index: number) => {
        const isTitle = text?.toLowerCase() === "title";
        return (
          <Input
            value={text}
            readOnly={isTitle}
            onChange={(e) => handleEditChange(index, "key", e.target.value)}
          />
        );
      },
    },
    {
      title: 'Value',
      dataIndex: 'value',
      key: 'value',
      width: '45%',
      render: (text: string, record: any, index: number) => (
        <Input
          value={text}
          onChange={(e) => handleEditChange(index, "value", e.target.value)}
        />
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: '20%',
      render: (_: any, record: any, index: number) => {
        const isTitle = record.key?.toLowerCase() === "title";
        return (
          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleRemoveMetadata(index)}
            disabled={isTitle}
          >
            Remove
          </Button>
        );
      },
    },
  ];

  return (
    <Modal
      title="Edit Document Metadata"
      open={show}
      onCancel={onHide}
      footer={[
        <Button key="cancel" onClick={onHide}>
          Cancel
        </Button>,
        <Button key="save" type="primary" onClick={handleSave}>
          Save Metadata
        </Button>,
      ]}
      centered
      width={800}
    >
      <Table
        columns={columns}
        dataSource={metadata}
        pagination={false}
        bordered
        size="small"
        style={{ marginBottom: 16 }}
      />

      <div style={{ padding: '16px 0', borderTop: '1px solid #f0f0f0' }}>
        <h4 style={{ marginBottom: 12 }}>Add Metadata</h4>
        <Space.Compact style={{ width: '100%' }}>
          <Input
            placeholder="Key"
            value={keyInput}
            onChange={(e) => setKeyInput(e.target.value)}
            style={{ width: '35%' }}
          />
          <Input
            placeholder="Value"
            value={valueInput}
            onChange={(e) => setValueInput(e.target.value)}
            style={{ width: '45%' }}
          />
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAddMetadata}
            style={{ width: '20%' }}
          >
            Add
          </Button>
        </Space.Compact>
      </div>
    </Modal>
  );
};

export default MetadataModal;
